import { UserApi } from "@/api/user.api";
import { Drop, IMAGE_MIME_TYPE } from "@/components/Drop/Drop";
import Navbar from "@/components/Navbar/Navbar";
import OrderDetailsModal from "@/components/OrderDetailsModal/OrderDetailsModal";
import { InCartBadge } from "@/components/InCartBadge/InCartBadge";
import { OrderBadgesScroll } from "@/components/OrderBadgesScroll/OrderBadgesScroll";
import {
  PLACEHOLDER_UPLOAD_CAPTION,
  PLACEHOLDER_UPLOAD_ORDER,
  isPlaceholderUpload,
} from "@/utils/constants/placeholderUploads";
import { CartService } from "@/stores/cart";
import { $selectedImage } from "@/stores/selectedImage";
import { clearSpecTemplate, peekSpecTemplate } from "@/stores/specTemplate";
import { $uploads, UploadsService } from "@/stores/uploads";
import { formatDateLong } from "@/utils/format";
import type { ImageFile, Order } from "@/utils/types/types";
import {
  ActionIcon,
  Box,
  Button,
  Center,
  Flex,
  Group,
  Loader,
  Pagination,
  Space,
  Stack,
  Text,
  Title,
  rem,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconDownload, IconEyeOff, IconTrash } from "@tabler/icons-react";
import { useStore } from "@nanostores/react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const PAGE_SIZE = 25;

export const Upload = () => {
  const recentUploads = useStore($uploads);
  const location = useLocation();
  const [isLoadingUploads, setIsLoadingUploads] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState<Order | undefined>();
  const [opened, { open, close }] = useDisclosure(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Staff request 2026-08-12: on the MAIN upload page, an ADMIN's upload
  // disappears from this list as soon as it's attached to any order, keeping
  // the working list to not-yet-ordered files (the manual eye-off hide never
  // got done; ~300 ordered uploads had accumulated here). Derived, not
  // stored: nothing is written to the upload, so the full list at
  // Profile → Uploads (which also shows manually-hidden files) still has
  // everything, and customer accounts are unaffected — non-admins see this
  // page exactly as before.
  // Reusable placeholders (utils/constants/placeholderUploads.ts) are pinned
  // to the top for admins and never fall out of the list when ordered.
  const visibleUploads = isAdmin
    ? [
        ...recentUploads
          .filter(isPlaceholderUpload)
          .sort(
            (a: { name: string }, b: { name: string }) =>
              PLACEHOLDER_UPLOAD_ORDER.indexOf(a.name) -
              PLACEHOLDER_UPLOAD_ORDER.indexOf(b.name)
          ),
        ...recentUploads.filter(
          u =>
            !isPlaceholderUpload(u) &&
            !(u.usedInOrders && u.usedInOrders.length > 0)
        ),
      ]
    : recentUploads;

  const totalPages = Math.max(1, Math.ceil(visibleUploads.length / PAGE_SIZE));
  const paginatedUploads = visibleUploads.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const handleDownload = (image: ImageFile) => {
    const link = document.createElement("a");
    link.href = image.rawUrl || image.url;
    link.download = image.name;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOrderNoClick = async (orderNo: string) => {
    const res = await UserApi.getOrderByNo(orderNo);
    if (res.data.order) {
      setSelectedOrder(res.data.order);
      open();
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);

    const loadUploads = async () => {
      setIsLoadingUploads(true);
      try {
        // Admin status resolves alongside the list so the used-in-order
        // filter applies on first paint (no flash of the unfiltered list).
        // Logged-out / failed lookups fall through to non-admin behavior.
        // Promise.resolve wrapper: getUser() returns a PLAIN OBJECT (not a
        // promise) when the stytch cookie is absent, so .catch can't hang
        // off it directly (TS2339 in the Netlify build).
        const [, userRes] = await Promise.all([
          UploadsService.getUploads(),
          Promise.resolve(UserApi.getUser()).catch(() => null),
        ]);
        setIsAdmin(Boolean(userRes?.data?.user?.isAdmin));
      } catch (error) {
        console.error("Failed to load uploads:", error);
      } finally {
        setIsLoadingUploads(false);
      }
    };

    loadUploads();
    if (queryParams.has("cart")) CartService.toggleCart(true);
  }, []);

  // "Same specs, new image": a pending template means the next image chosen
  // here opens the visualizer pre-configured. Say so until it's used/cancelled.
  const [pendingSpecs, setPendingSpecs] = useState(() => peekSpecTemplate());

  return (
    <div>
      <Navbar upload />
      {pendingSpecs && (
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 30,
            background: "#E8F7F7",
            borderBottom: "1px solid #90D9D9",
            color: "#246B6B",
            padding: "10px 16px",
            textAlign: "center",
            fontSize: 14,
          }}
        >
          <strong>Applying specs from your last item:</strong>{" "}
          {pendingSpecs.label || "previous item"} — choose the image for the
          next piece.{" "}
          <button
            type="button"
            onClick={() => {
              clearSpecTemplate();
              setPendingSpecs(null);
            }}
            style={{
              background: "none",
              border: "none",
              color: "#246B6B",
              textDecoration: "underline",
              cursor: "pointer",
              padding: 0,
              font: "inherit",
            }}
          >
            Cancel
          </button>
        </div>
      )}
      <Stack
        align="center"
        gap={0}
        justify="center"
        style={{ paddingTop: "50px" }}
      >
        <Box>
          <Title fw={700} order={1} ta="center">
            Upload Images
          </Title>
          <Space h={rem(15)} />
          <Text c="gray.6" fw={300} size="sm" ta="center">
            Upload an image, and fully customize it based on <br />
            product type, framing options and other categories
          </Text>
        </Box>
        <Space h={rem(40)} />

        <Stack align="center" justify="center">
          <Box h={200} w={{ base: "90vw", md: "40vw" }}>
            <Drop />
          </Box>
          <Box>
            <Text c="gray.6" fw={300} size="sm" ta="center" px="1rem">
              Upload up to <b>500MB</b> image with the format of{" "}
              <b>
                {IMAGE_MIME_TYPE.map(mime => mime.split("/")[1]).join(", ")}
              </b>
            </Text>
            <Text c="gray.6" fw={300} size="sm" ta="center" px="1rem">
              Your file should be in <b>8-bit RGB</b>, preferably{" "}
              <b>Adobe RGB (1998)</b>, or <b>sRGB</b>.
            </Text>
          </Box>
          <Space h={rem(15)} />
          {isLoadingUploads ? (
            <Center style={{ minHeight: rem(200) }}>
              <Stack align="center" gap="md">
                <Loader size="lg" color="#489B9B" />
                <Text c="gray.6" fw={300} size="sm">
                  Loading your recent uploads...
                </Text>
              </Stack>
            </Center>
          ) : (
            <Stack gap="md" className="w-[90vw] md:w-[min(80vw,700px)]">
              {paginatedUploads.map(image => (
                <Box
                  key={image.url}
                  p="md"
                  style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: rem(8),
                  }}
                >
                  <Flex gap="md" align="center" wrap="wrap">
                    {/* Image thumbnail */}
                    <Box
                      className="group/image"
                      style={{
                        height: rem(80),
                        flexShrink: 0,
                        overflow: "hidden",
                        borderRadius: rem(4),
                        position: "relative",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        $selectedImage.set(image);
                        window.location.href = "/visualizer";
                      }}
                    >
                      <img
                        src={image.thumbnailUrl || image.url}
                        alt={image.name}
                        style={{
                          // Size from the image's own aspect ratio. width:100%
                          // of the width-less parent Box is circular sizing —
                          // Chrome guesses, Safari squashes the thumbnail.
                          height: "100%",
                          width: "auto",
                          maxWidth: "220px",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                      <div className="absolute inset-0 grid place-items-center content-center bg-black bg-opacity-0 leading-none text-white opacity-0 transition-opacity duration-200 group-hover/image:bg-opacity-50 group-hover/image:opacity-100">
                        <p className="text-sm font-bold">Open in</p>
                        <p className="text-sm">Visualizer</p>
                      </div>
                    </Box>

                    {/* Image details */}
                    <Flex
                      direction="column"
                      justify="space-between"
                      style={{ flex: 1, minWidth: rem(200), height: rem(80) }}
                    >
                      <p className="text-md break-all leading-none">
                        {image.name}
                      </p>
                      <p className="text-sm leading-none text-[#636363]">
                        Uploaded: {formatDateLong(new Date(image.updatedAt))}
                      </p>
                      {isAdmin && isPlaceholderUpload(image) ? (
                        <p className="text-sm italic leading-none text-[#246B6B]">
                          {PLACEHOLDER_UPLOAD_CAPTION[image.name]}
                        </p>
                      ) : image.inCarts && image.inCarts.length > 0 ? (
                        <Flex gap="xs" align="center" wrap="wrap">
                          <InCartBadge inCarts={image.inCarts} />
                          {image.usedInOrders &&
                            image.usedInOrders.length > 0 && (
                              <OrderBadgesScroll
                                orderNos={image.usedInOrders}
                                onOrderNoClick={handleOrderNoClick}
                              />
                            )}
                        </Flex>
                      ) : image.usedInOrders &&
                        image.usedInOrders.length > 0 ? (
                        <OrderBadgesScroll
                          orderNos={image.usedInOrders}
                          onOrderNoClick={handleOrderNoClick}
                        />
                      ) : (
                        <p className="text-sm italic leading-none text-[#636363]">
                          Not used in any orders yet
                        </p>
                      )}
                    </Flex>

                    {/* Advance CTA (2026-08-13): the thumbnail hover overlay
                        is the only path into the visualizer and hover doesn't
                        exist on touch devices — customers stalled here. An
                        always-visible button makes the next step legible:
                        full-width on phones, inline on desktop. The thumbnail
                        hover overlay stays as a secondary affordance. */}
                    <Button
                      className="w-full md:w-auto"
                      color="#489B9B"
                      size="sm"
                      onClick={() => {
                        $selectedImage.set(image);
                        window.location.href = "/visualizer";
                      }}
                    >
                      Choose Size &amp; Framing →
                    </Button>

                    {/* Action buttons */}
                    <Flex
                      direction="column"
                      justify="space-between"
                      align="center"
                      gap={rem(4)}
                    >
                      <Button
                        variant="light"
                        size="lg"
                        h={rem(30)}
                        w={rem(30)}
                        style={{ padding: 0 }}
                        onClick={() => handleDownload(image)}
                      >
                        <IconDownload size={18} />
                      </Button>
                      {!(isAdmin && isPlaceholderUpload(image)) && (
                        <>
                          <ActionIcon
                            variant="light"
                            color="gray"
                            size={rem(30)}
                            onClick={async () => {
                              await UploadsService.hideImageOptimistic(image);
                            }}
                          >
                            <IconEyeOff size={18} />
                          </ActionIcon>
                          <ActionIcon
                            variant="light"
                            color="red"
                            size={rem(30)}
                            onClick={async () => {
                              await UploadsService.deleteImageOptimistic(image);
                            }}
                          >
                            <IconTrash size={18} />
                          </ActionIcon>
                        </>
                      )}
                    </Flex>
                  </Flex>
                </Box>
              ))}
              {totalPages > 1 && (
                <Group justify="space-between" align="center" mt="sm">
                  <Text c="gray.6" size="sm">
                    {visibleUploads.length} upload
                    {visibleUploads.length !== 1 ? "s" : ""} &mdash; page{" "}
                    {currentPage} of {totalPages}
                  </Text>
                  <Pagination
                    value={currentPage}
                    onChange={setCurrentPage}
                    total={totalPages}
                    color="#489B9B"
                    size="sm"
                  />
                </Group>
              )}
            </Stack>
          )}
          <Space h={rem(50)} />
        </Stack>
      </Stack>
      <OrderDetailsModal order={selectedOrder} opened={opened} close={close} />
    </div>
  );
};
